import type { Course, CourseLevel } from "@/lib/courses";

/**
 * Student-facing course shapes: enrolment, credentials and recommendations.
 * The shared course vocabulary itself lives in @/lib/courses (used by the admin and
 * creator builders too). Shapes follow scripts/courses/API_CONTRACT.md.
 */

export type LessonStatus = "not_started" | "in_progress" | "completed";

export interface Enrollment {
  enrolledAt: string;
  completedAt: string | null;
  completedLessons: number;
  totalLessons: number;
  progress: number;
  lastActivityAt: string | null;
}

export interface CourseWithEnrollment extends Course {
  enrollment: Enrollment | null;
}

export interface CourseLessonRef {
  id: string;
  slug: string;
  title: string;
  description?: string | null;
  emoji?: string | null;
  estimatedMinutes?: number;
  xpReward?: number;
}

export interface CourseModule {
  id: string;
  sortOrder: number;
  required: boolean;
  lesson: CourseLessonRef;
  progress: { status: LessonStatus; score: number | null; completedAt: string | null } | null;
}

export interface CourseCredential {
  code: string;
  title: string;
  issuedAt?: string;
}

/** GET /api/courses/:slug as the student sees it, with progress and credential. */
export interface CourseDetailPayload {
  course: Course;
  modules: CourseModule[];
  enrollment: Enrollment | null;
  credential: CourseCredential | null;
  prerequisitesMet: boolean;
  missingPrerequisites: Array<{ slug: string; title: string }>;
}

export interface InterestOption {
  id: string;
  label: string;
  emoji: string;
}

export interface Recommendation {
  course: CourseWithEnrollment;
  reasons: string[];
  score: number;
}

export interface RecommendationsPayload {
  interestsSet: boolean;
  forYou: Recommendation[];
  nextLessons: Array<{
    lesson: { id: string; slug: string; title: string; emoji: string | null; subject: string; topic: string; estimatedMinutes: number; xpReward: number };
    reasons: string[];
  }>;
  continueCourse: { course: CourseWithEnrollment; nextLesson: CourseLessonRef } | null;
}

/** Colour for each course level badge, in the kid-friendly palette the lessons use. */
export const LEVEL_BADGE: Record<CourseLevel, { label: string; className: string }> = {
  starter: {
    label: "Starter",
    className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  },
  explorer: {
    label: "Explorer",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  },
  expert: {
    label: "Expert",
    className: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  },
};

export function levelBadge(level: string | null | undefined) {
  return LEVEL_BADGE[(level as CourseLevel) ?? "starter"] ?? LEVEL_BADGE.starter;
}

/** The lesson player lives at /lessons/:slug. Courses pass their slug so the player shows context. */
export function courseLessonPath(lessonSlug: string, courseSlug: string): string {
  return `/lessons/${lessonSlug}?course=${encodeURIComponent(courseSlug)}`;
}

export function credentialPath(code: string): string {
  return `/credentials/${encodeURIComponent(code)}`;
}

export function credentialShareUrl(code: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://edufocalacademy.com";
  return `${origin}${credentialPath(code)}`;
}

export function formatIssuedDate(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}
